import React from "react"
import TransactionRecord from "./TransactionRecord"
import { ResourceBookingSchema, ResourceType } from "../data/types";
import { capitalizeString, TransactionSortField } from "../utils/helpers";
import Pagination from "../components/Pagination";
import SortableHeader, { SortDirection } from "../components/SortableHeader";

export type { TransactionSortField, SortDirection };

type TransactionsListProps = {
  resourceType: ResourceType
  transactions: ResourceBookingSchema[],
  isRequests?: boolean,
  currentPage: number,
  currentTransactions: ResourceBookingSchema[],
  totalPages: number,
  itemsPerPage: number,
  handlePageChange: (page: number) => void,
  sortField: TransactionSortField
  sortDirection: SortDirection
  onSort: (field: TransactionSortField) => void
  hasSearchFilter?: boolean
}

const TRANSACTION_ACTIONS_HEADER =
  'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';

const getTransactionColumnCount = (resourceType: ResourceType): number => {
  let count = 6

  if (resourceType === 'training' || resourceType === 'expert') {
    count += 1
  }

  if (resourceType && ['kitchen', 'training', 'expert'].includes(resourceType)) {
    count += 1
  }

  if (resourceType === 'kitchen') {
    count += 2
  }

  return count
}

const TransactionsList: React.FC<TransactionsListProps> = ({
  resourceType,
  transactions,
  isRequests,
  currentPage,
  currentTransactions,
  totalPages,
  itemsPerPage,
  handlePageChange,
  sortField,
  sortDirection,
  onSort,
  hasSearchFilter,
}) => {
  let dateFieldName = ""
  switch (resourceType) {
    case 'training':
      dateFieldName = "Date"
      break
    case 'expert':
      dateFieldName = "Session Date"
      break
  }

  const emptyMessage = hasSearchFilter ? 'No matching transactions' : 'No Transactions'
  const columnCount = getTransactionColumnCount(resourceType)

  return (
    <div>
      <table className="table-auto my-3 min-w-full divide-y divide-gray-200 font-open-sans">
        <thead className="bg-gray-50">
          <tr>
            <SortableHeader
              label="Member"
              field="user_full_name"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={onSort}
            />

            <SortableHeader
              label={capitalizeString(resourceType)}
              field="resource_name"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={onSort}
            />

            {dateFieldName && (
              <SortableHeader
                label={dateFieldName}
                field="start_time"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={onSort}
              />
            )}

            {resourceType && ['kitchen', 'training', 'expert'].includes(resourceType) && (
              <SortableHeader
                label="Hours"
                field="total_hours"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={onSort}
              />
            )}

            {resourceType === 'kitchen' && (
              <>
                <SortableHeader
                  label="Rate"
                  field="plan_rate"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={onSort}
                />
                <SortableHeader
                  label="Subtotal"
                  field="subtotal_cost"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={onSort}
                />
              </>
            )}

            <SortableHeader
              label="Total"
              field="total_cost"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={onSort}
            />
            <SortableHeader
              label="Submitted"
              field="created"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={onSort}
            />
            <SortableHeader
              label="Status"
              field="status"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={onSort}
            />
            <th scope="col" className={TRANSACTION_ACTIONS_HEADER}>
              Actions
            </th>
          </tr>
        </thead>

        <tbody className="bg-white divide-y divide-gray-200">
          {currentTransactions?.length ? (
            currentTransactions.map((transaction: ResourceBookingSchema, i: number) => (
              <TransactionRecord
                key={transaction.id ?? i}
                request={transaction}
                isRequests={isRequests}
                resourceType={resourceType}
              />
            ))
          ) : (
            <tr>
              <td colSpan={columnCount} className="px-6 py-4 text-sm text-gray-500 text-center">
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {transactions.length > itemsPerPage && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={transactions.length}
            itemsPerPage={itemsPerPage}
            showInfo={true}
            itemLabel="Transactions"
            showFirstLast={true}
            maxVisiblePages={5}
          />
        </div>
      )}
    </div>
  );
};

export default TransactionsList;
